import { Button, SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { ReactNode } from "react";
import {
  colorLandingBrand,
  colorLandingBrandStrong,
  colorLandingInk,
  colorLandingLine,
  colorLandingSurface,
  fontLandingSans,
  radiusLandingControl,
  shadowLandingBrand,
} from "src/design/tokens";

type LandingButtonVariant = "brand" | "ghost" | "ghostOnDark";

interface LandingButtonProps {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: LandingButtonVariant;
  startIcon?: ReactNode;
  fullWidth?: boolean;
  type?: "button" | "submit";
  disabled?: boolean;
  sx?: SxProps;
}

const VARIANT_STYLES: Record<
  LandingButtonVariant,
  SystemStyleObject<Theme>
> = {
  brand: {
    backgroundColor: colorLandingBrand,
    color: colorLandingSurface,
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: colorLandingBrandStrong,
      boxShadow: shadowLandingBrand,
    },
  },
  ghost: {
    backgroundColor: colorLandingSurface,
    color: colorLandingInk,
    border: `1px solid ${colorLandingLine}`,
    "&:hover": {
      backgroundColor: colorLandingSurface,
      borderColor: colorLandingInk,
    },
  },
  ghostOnDark: {
    backgroundColor: "transparent",
    color: colorLandingSurface,
    border: `1px solid ${colorLandingSurface}80`,
    "&:hover": {
      backgroundColor: `${colorLandingSurface}1A`,
      borderColor: colorLandingSurface,
    },
  },
};

/**
 * The landing page's single button shape: the brand pill, the light ghost and
 * the ghost used on the dark hero. Kept separate from the in-app buttons so the
 * public page can carry its own type scale and radius.
 */
export const LandingButton = ({
  label,
  onClick,
  href,
  variant = "brand",
  startIcon,
  fullWidth,
  type = "button",
  disabled,
  sx,
}: LandingButtonProps) => (
  <Button
    onClick={onClick}
    href={href}
    component={href ? "a" : "button"}
    type={href ? undefined : type}
    disabled={disabled}
    fullWidth={fullWidth}
    startIcon={startIcon}
    disableElevation
    sx={[
      {
        fontFamily: fontLandingSans,
        fontWeight: 600,
        fontSize: 15,
        textTransform: "none",
        borderRadius: radiusLandingControl,
        px: 2.75,
        py: 1.6,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        transition: "background .15s, box-shadow .15s, border-color .15s",
        "&:active": { transform: "translateY(1px)" },
        "&.Mui-disabled": { opacity: 0.6 },
      },
      VARIANT_STYLES[variant],
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {label}
  </Button>
);
